import { useRef, useState, useEffect } from 'react';
import { message } from 'antd';
import Markdown from 'react-markdown';
import markdownDocx, { Packer } from 'markdown-docx';
import { toPng } from 'html-to-image';
import { HistoryItem } from '@/utils/store/store';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '@/constants/messages';
import { copyToClipboard, handleError, handleSuccess } from '@/utils/helpers';
import dayjs from 'dayjs';

interface HistoryResultProps {
  historyItem: HistoryItem;
  onClose: () => void;
  /** 自动导出图片模式：挂载后自动截图导出，完成后自动关闭 */
  autoExportImage?: boolean;
}

/**
 * 历史记录结果展示组件
 * 仅用于历史记录弹窗场景，Notion 风格
 * 支持 autoExportImage 模式：弹窗打开后自动导出图片并关闭
 */
const HistoryResult = ({ historyItem, onClose, autoExportImage = false }: HistoryResultProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExportingImage, setIsExportingImage] = useState(false);

  // 解析历史记录内容
  let content = '';
  try {
    content = JSON.parse(historyItem.result)['default'] || '';
  } catch {
    content = '';
  }

  // 解析标题
  const title = historyItem.title || (() => {
    try { return JSON.parse(historyItem.result)['default']?.slice(0, 30) + '...'; }
    catch { return '会议纪要'; }
  })();

  // 导出为 docx
  const handleExport = async () => {
    if (!content) {
      message.info('内容为空');
      return;
    }
    try {
      const doc = await markdownDocx(content);
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${historyItem.title || '会议纪要'}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      handleSuccess(SUCCESS_MESSAGES.DOCX_DOWNLOAD_SUCCESS);
    } catch (error) {
      handleError(error, ERROR_MESSAGES.DOCX_GENERATION_FAILED);
    }
  };

  // 复制内容
  const handleCopy = () => {
    if (!content) {
      message.info('内容为空');
      return;
    }
    copyToClipboard(content, SUCCESS_MESSAGES.COPY_SUCCESS);
  };

  // 导出为图片
  // 直接对弹窗内已渲染的元素截图，避免 cloneNode 丢失 Tailwind CSS 变量/样式
  const modalRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const handleExportImage = async () => {
    if (!content || !modalRef.current) {
      message.info('内容为空');
      return;
    }
    setIsExportingImage(true);

    const modalEl = modalRef.current;
    const articleEl = contentRef.current;
    const toolbarEl = toolbarRef.current;
    const closeBtn = closeBtnRef.current;

    // 保存原始样式
    const origModal = modalEl.style.cssText;
    const origArticle = articleEl ? articleEl.style.cssText : '';

    try {
      // 临时隐藏工具栏和关闭按钮
      if (toolbarEl) toolbarEl.style.display = 'none';
      if (closeBtn) closeBtn.style.display = 'none';

      // 临时插入 NivoWork logo 到弹窗最右上角
      const logoEl = document.createElement('div');
      logoEl.setAttribute('data-export-logo', '');
      logoEl.style.cssText = 'position:absolute;top:24px;right:28px;display:flex;align-items:center;gap:10px;z-index:10;';
      logoEl.innerHTML = `<div style="width:32px;height:32px;background:#000;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:18px;">N</div><span style="font-size:17px;font-weight:600;letter-spacing:-0.025em;color:#37352F;">NivoWork</span>`;
      modalEl.appendChild(logoEl);

      // 临时展开：去掉 max-height 和 overflow 限制
      modalEl.style.maxHeight = 'none';
      modalEl.style.overflow = 'visible';
      if (articleEl) {
        articleEl.style.maxHeight = 'none';
        articleEl.style.overflow = 'visible';
      }

      await new Promise(resolve => requestAnimationFrame(resolve));

      const dataUrl = await toPng(modalEl, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });

      // 移除临时 logo
      if (logoEl.parentNode) modalEl.removeChild(logoEl);

      const link = document.createElement('a');
      link.download = `${historyItem.title || '会议纪要'}.png`;
      link.href = dataUrl;
      link.click();
      handleSuccess('图片导出成功');
    } catch (error) {
      handleError(error, '图片导出失败');
    } finally {
      // 恢复所有样式
      if (toolbarEl) toolbarEl.style.display = '';
      if (closeBtn) closeBtn.style.display = '';
      modalEl.style.cssText = origModal;
      if (articleEl) {
        articleEl.style.cssText = origArticle;
      }
      // 确保 logo 被移除（异常情况兜底）
      const leftover = modalEl.querySelector('[data-export-logo]');
      if (leftover) leftover.remove();
      setIsExportingImage(false);
    }
  };

  // 自动导出图片模式
  useEffect(() => {
    if (autoExportImage && content && modalRef.current) {
      // 等待 DOM 完全渲染后再截图
      const timer = setTimeout(() => {
        handleExportImage().then(() => onClose());
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoExportImage]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" />

      {/* 弹窗主体 */}
      <div
        ref={modalRef}
        className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] border border-[#E9E9E7] w-[720px] max-w-[90vw] max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部标题栏 */}
        <div className="flex items-center justify-between px-7 pt-6 pb-4 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-1">
              <svg className="w-4.5 h-4.5 text-[#91918E] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              <h2 className="text-base font-semibold text-[#37352F] truncate">{title}</h2>
            </div>
            {historyItem.createTime && (
              <span className="text-xs text-[#91918E] ml-7">
                {dayjs(historyItem.createTime).format('YYYY年M月D日 HH:mm')}
              </span>
            )}
          </div>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-[#91918E] hover:text-[#37352F] hover:bg-[#F7F7F5] transition-colors cursor-pointer flex-shrink-0 ml-4"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 工具栏 */}
        <div ref={toolbarRef} className="flex items-center gap-0.5 px-7 pb-3 border-b border-[#E9E9E7] flex-shrink-0">
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
            </svg>
            复制
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            导出 Word
          </button>
          <button
            onClick={handleExportImage}
            disabled={isExportingImage}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a2.25 2.25 0 002.25-2.25V5.25a2.25 2.25 0 00-2.25-2.25H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
            </svg>
            {isExportingImage ? '导出中...' : '导出图片'}
          </button>
        </div>

        {/* 内容区域 */}
        {content ? (
          <article
            ref={contentRef}
            className="flex-1 overflow-y-auto px-7 py-5 relative"
            style={{ scrollbarWidth: 'thin' }}
          >
            <div className="markdown space-y-2 text-[#37352F] text-sm leading-relaxed">
              <Markdown>{content}</Markdown>
            </div>
          </article>
        ) : (
          <div className="flex-1 flex items-center justify-center py-20">
            <span className="text-sm text-[#91918E]">暂无内容</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryResult;
